import { useEffect } from 'react';

import getFocusableElements from 'hb-react/shared/utils/getFocusableElements';
import getNextFocusableElements from 'hb-react/shared/utils/getNextFocusableElement';

export const useFocusTrap = (
  containerRef: HTMLElement | null,
  isActive: boolean,
): void => {
  useEffect(() => {
    if (!isActive || !containerRef) return;

    const focusableElements = getFocusableElements(containerRef);
    const firstFocusable = focusableElements[0];

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      // Prevent default Tab behavior and force focus back to container
      if (!containerRef?.contains(document.activeElement)) {
        e.preventDefault();
        firstFocusable?.focus();

        return;
      }

      e.preventDefault();
      const direction = e.shiftKey ? -1 : 1;
      const nextElement = getNextFocusableElements(containerRef, direction);

      nextElement?.focus();
    };

    const handleFocus = (e: FocusEvent) => {
      if (!containerRef?.contains(e.target as Node)) {
        firstFocusable?.focus();
      }
    };

    firstFocusable?.focus();

    document.addEventListener('keydown', handleTabKey);
    document.addEventListener('focusin', handleFocus);

    return () => {
      document.removeEventListener('keydown', handleTabKey);
      document.removeEventListener('focusin', handleFocus);
    };
  }, [isActive, containerRef]);
};
